// src/services/smsCampaignService.js
import ApiService from './api'
import AppURLs from '../config/urls'
import { SMSCampaignModel } from '../utils/smsCampaignModel'

class SMSCampaignService {
  
  /**
   * Get all SMS campaigns with pagination support
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getAllSMSCampaigns(params = {}) {
    try {
      console.log('📱 Fetching all SMS campaigns with params:', params)
      
      const response = await ApiService.get(AppURLs.SMS_CAMPAIGNS.GET_ALL, params)
      console.log('✅ SMS campaigns fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching SMS campaigns:', error)
      throw error
    }
  }

  /**
   * Get SMS campaign by ID
   * @param {string} id - Campaign ID
   * @returns {Promise<Object>} - API response
   */
  async getSMSCampaignById(id) {
    try {
      console.log('📱 Fetching SMS campaign by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SMS_CAMPAIGNS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ SMS campaign fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching SMS campaign:', error)
      throw error
    }
  }

  /**
   * Create new SMS campaign
   * @param {Object} campaignData - Campaign data to create
   * @returns {Promise<Object>} - API response
   */
  async createSMSCampaign(campaignData) {
    try {
      console.log('📱 Creating new SMS campaign with data:', campaignData)
      
      // Validate required fields
      this.validateSMSCampaignData(campaignData)
      
      const response = await ApiService.post(AppURLs.SMS_CAMPAIGNS.CREATE, campaignData)
      console.log('✅ SMS campaign created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating SMS campaign:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      
      throw error
    }
  }

  /**
   * Update existing SMS campaign
   * @param {string} id - Campaign ID to update
   * @param {Object} campaignData - Updated campaign data
   * @returns {Promise<Object>} - API response
   */
  async updateSMSCampaign(id, campaignData) {
    try {
      console.log('📱 Updating SMS campaign ID:', id, 'with data:', campaignData)
      
      // Validate required fields for update
      this.validateSMSCampaignDataForUpdate(campaignData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.SMS_CAMPAIGNS.UPDATE, { id })
      const response = await ApiService.patch(endpoint, campaignData)
      console.log('✅ SMS campaign updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating SMS campaign:', error)
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      
      throw error
    }
  }

  /**
   * Delete SMS campaign
   * @param {string} id - Campaign ID to delete
   * @returns {Promise<Object>} - API response
   */
  async deleteSMSCampaign(id) {
    try {
      console.log('📱 Deleting SMS campaign ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SMS_CAMPAIGNS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ SMS campaign deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting SMS campaign:', error)
      throw error
    }
  }

  /**
   * Get SMS logs for campaigns
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getSMSLogs(params = {}) {
    try {
      console.log('📱 Fetching SMS logs with params:', params)
      
      const response = await ApiService.get(AppURLs.SMS_CAMPAIGNS.LOGS, params)
      console.log('✅ SMS logs fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching SMS logs:', error)
      throw error
    }
  }

  /**
   * Run/Execute SMS campaign
   * @param {string} id - Campaign ID to run
   * @returns {Promise<Object>} - API response
   */
  async runSMSCampaign(id) {
    try {
      console.log('📱 Running SMS campaign ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SMS_CAMPAIGNS.RUN, { id })
      const response = await ApiService.post(endpoint)
      console.log('✅ SMS campaign started successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error running SMS campaign:', error)
      throw error
    }
  }

  /**
   * Retry SMS campaign
   * @param {string} id - Campaign ID to retry
   * @returns {Promise<Object>} - API response
   */
  async retrySMSCampaign(id) {
    try {
      console.log('📱 Retrying SMS campaign ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SMS_CAMPAIGNS.RETRY, { id })
      const response = await ApiService.post(endpoint)
      console.log('✅ SMS campaign retry started successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error retrying SMS campaign:', error)
      throw error
    }
  }

  /**
   * Get SMS campaign statistics
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} - API response
   */
  async getSMSCampaignStats(params = {}) {
    try {
      console.log('📱 Fetching SMS campaign stats with params:', params)
      
      const response = await ApiService.get(AppURLs.SMS_CAMPAIGNS.STATS, params)
      console.log('✅ SMS campaign stats fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching SMS campaign stats:', error)
      throw error
    }
  }

  /**
   * Bulk delete SMS campaigns
   * @param {Array} ids - Array of campaign IDs to delete
   * @returns {Promise<Object>} - API response
   */
  async bulkDeleteSMSCampaigns(ids) {
    try {
      console.log('📱 Bulk deleting SMS campaigns:', ids)
      const response = await ApiService.post(AppURLs.SMS_CAMPAIGNS.BULK_DELETE, { ids })
      console.log('✅ SMS campaigns bulk deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting SMS campaigns:', error)
      throw error
    }
  }

  /**
   * Validate SMS campaign data for creation
   * @param {Object} data - Campaign data to validate
   * @throws {Error} - Validation error
   */
  validateSMSCampaignData(data) {
    console.log('🔍 Validating SMS campaign data:', data)

    // Use the model's validation method
    const validationResult = SMSCampaignModel.validate(data)
    
    if (!validationResult.isValid) {
      console.error('❌ SMS campaign validation failed:', validationResult.errors)
      throw new Error(validationResult.errors.join(', '))
    }

    console.log('✅ SMS campaign data validation passed')
  }

  /**
   * Validate SMS campaign data for update
   * @param {Object} data - Campaign data to validate
   * @throws {Error} - Validation error
   */
  validateSMSCampaignDataForUpdate(data) {
    console.log('🔍 Validating SMS campaign data for update:', data)

    // For updates, we allow partial data
    const validationResult = SMSCampaignModel.validateUpdate(data)
    
    if (!validationResult.isValid) {
      console.error('❌ SMS campaign update validation failed:', validationResult.errors)
      throw new Error(validationResult.errors.join(', '))
    }

    console.log('✅ SMS campaign update data validation passed')
  }

  /**
   * Format SMS campaign data for display
   * @param {Object} campaign - Raw campaign data from API
   * @returns {Object} - Formatted campaign data
   */
  formatSMSCampaignForDisplay(campaign) {
    return SMSCampaignModel.formatForDisplay(campaign)
  }

  /**
   * Get campaign status display information
   * @param {string} status - Campaign status
   * @returns {Object} - Status display configuration
   */
  getStatusConfig(status) {
    return SMSCampaignModel.getStatusConfig(status)
  }

  /**
   * Parse API response and extract campaign data
   * @param {Object} response - API response
   * @returns {Object} - Parsed campaign data
   */
  parseResponse(response) {
    try {
      console.log('📱 Parsing SMS campaign response:', response)

      // Handle different response structures
      if (response?.data?.smsCampaigns) {
        return {
          campaigns: response.data.smsCampaigns,
          totalResults: response.data.totalResults || response.data.smsCampaigns.length,
          success: response.success
        }
      }

      if (response?.data?.smsCampaign) {
        return {
          campaign: response.data.smsCampaign,
          success: response.success
        }
      }

      if (response?.data?.smsLogs) {
        return {
          logs: response.data.smsLogs,
          totalResults: response.data.totalResults || response.data.smsLogs.length,
          success: response.success
        }
      }

      // Handle generic response
      return {
        data: response.data,
        success: response.success,
        message: response.message
      }

    } catch (error) {
      console.error('❌ Error parsing SMS campaign response:', error)
      throw new Error('Failed to parse API response')
    }
  }
}

export default new SMSCampaignService()